
export interface FeatureList {
    type: string;
    features: Feature[];
    attribution: string;
}

export interface Feature {
    type: string;
    geometry: Geometry;
    properties: Properties;
}

export interface Geometry {
    coordinates: number[];
    type: string;
}

export interface Properties {
    name: string;
    mapbox_id: string;
    feature_type: string;
    address: string;
    full_address: string;
    place_formatted: string;
    context: Context;
    coordinates: Coordinates;
    language: string;
    maki: string;
    poi_category: string[];
    poi_category_ids: string[];
    external_ids: ExternalIDS;
    metadata: Metadata;
    brand?: string[];
    brand_id?: string[];
}

export interface Context {
    country: Country;
    region: Region;
    postcode: Neighborhood;
    place: Neighborhood;
    neighborhood: Neighborhood;
    street: Neighborhood;
    address?: Address;
}

export interface Address {
    name: string;
    address_number: string;
    street_name: string;
}

export interface Country {
    name: string;
    country_code: string;
    country_code_alpha_3: string;
}

export interface Neighborhood {
    name: string;
}

export interface Region {
    name: string;
    region_code: string;
    region_code_full: string;
}

export interface Coordinates {
    latitude: number;
    longitude: number;
    routable_points: RoutablePoint[];
}

export interface RoutablePoint {
    name: string;
    latitude: number;
    longitude: number;
}

export interface ExternalIDS {
    dataplor: string;
}

export interface Metadata {
    phone: string;
    website?: string;
    open_hours?: OpenHours;
}

export interface OpenHours {
    periods: Period[];
}

export interface Period {
    open: Close;
    close: Close;
}

export interface Close {
    day: number;
    time: string;
}

export interface MapboxContext {
    id: string;
    mapbox_id?: string;
    text: string;
    wikidata?: string;
    short_code?: string;
}

export interface MapboxGeometry {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
}

export interface MapboxFeature {
    id: string;
    type: "Feature";
    place_type: string[];
    relevance: number;
    properties: {
        accuracy?: string;
        mapbox_id?: string;
        [key: string]: any;
    };
    text: string;
    place_name: string;
    center: [number, number];
    geometry: MapboxGeometry;
    context?: MapboxContext[];
    bbox?: [number, number, number, number];
}

export interface MapboxBatchResponseItem {
    features: MapboxFeature[];
    query?: any;
}

export interface MapboxBatchResponse {
    batch: MapboxBatchResponseItem[];
}

export interface GeocodingResponse {
    type: string;
    features: GeocodingFeature[];
    attribution: string;
}

export interface GeocodingFeature {
    type: string;
    id: string;
    geometry: {
        type: string;
        coordinates: [number, number]; // [lng, lat]
    };
    properties: {
        name: string;
        mapbox_id: string;
        feature_type: string;
        place_formatted: string;
        coordinates: {
            longitude: number;
            latitude: number;
        };
        context: Context;
        [key: string]: any;
    };
}

const accessToken = process.env.REACT_APP_MAPBOX_API_KEY!; 
export async function fetchNearbyBusinesses( lat: number, lng: number ): Promise<FeatureList> {
    const token = accessToken;
    const url = new URL( 'https://api.mapbox.com/search/searchbox/v1/reverse' );

    url.searchParams.set( "types", "poi" );
    url.searchParams.set( "longitude", lng.toString() );
    url.searchParams.set( "latitude", lat.toString() );
    url.searchParams.set( "access_token", token );
    url.searchParams.set( "limit", "5" ); // Optional: fetch more or fewer results

    const res = await fetch( url.toString() );
    if( !res.ok )
        throw new Error( `HTTP ${res.status}` );
    return res.json() as Promise<FeatureList>;
}

export async function fetchNearbyAddress( lat: number, lng: number ): Promise<FeatureList> {
    const token = accessToken;
    const url = new URL( 'https://api.mapbox.com/search/searchbox/v1/reverse' );
    url.searchParams.set( "types", "address" );
    url.searchParams.set( "longitude", lng.toString() );
    url.searchParams.set( "latitude", lat.toString() );
    url.searchParams.set( "access_token", token );
    url.searchParams.set( "limit", "1" ); // Optional: fetch more or fewer results

    const res = await fetch( url.toString() );
    if( !res.ok )
        throw new Error( `HTTP ${res.status}` );
    return res.json() as Promise<FeatureList>;
}


export async function geocodeLocation( locationName: string ): Promise<{ lat: number; lng: number } | null> {
    const token = accessToken;
    const url = new URL( 'https://api.mapbox.com/search/geocode/v6/forward' );
    
    url.searchParams.set( "q", locationName );
    url.searchParams.set( "access_token", token );
    url.searchParams.set( "limit", "1" );

    const res = await fetch( url.toString() );
    if( !res.ok )
        throw new Error( `HTTP ${res.status}` );
    
    const data = await res.json() as GeocodingResponse;    
    if( data.features && data.features.length > 0 ) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        return {
            lat: latitude,
            lng: longitude
        };
    }    
    return null;
}